import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLoading } from '../context/LoadingContext';

gsap.registerPlugin(ScrollTrigger);

const AnimatedBackground = React.memo(() => {
  const containerRef = useRef(null);
  const blobsRef = useRef([]);
  const particlesRef = useRef([]);
  const animationsRef = useRef([]);
  const { isInitialized } = useLoading();

  // Memoize configuration for better performance
  const config = useMemo(() => ({
    particleCount: 14, // Increased for richer backdrop
    animationDuration: { 
      fast: 8, 
      slow: 15 
    },
    movements: {
      blob: 35, // Increased movement range for deeper parallax
      particle: 30
    },
    scales: {
      blob: { min: 0.9, max: 1.1 }, // Reduced scale range
      particle: { min: 0.2, max: 0.3 }
    }
  }), []);

  // Optimized animation creation
  const createOptimizedAnimations = useCallback(() => {
    const blobs = blobsRef.current.filter(Boolean);
    const particles = particlesRef.current.filter(Boolean);

    // Clear existing animations
    animationsRef.current.forEach(anim => anim.kill());
    animationsRef.current = [];

    // Set initial state with GPU-accelerated properties
    gsap.set([...blobs, ...particles], {
      opacity: 0,
      scale: 0.8,
      force3D: true, // Force GPU acceleration
      transformOrigin: "center center"
    });

    // Create blob animations with reduced complexity
    blobs.forEach((blob, index) => {
      const tl = gsap.timeline();
      
      // Entrance animation
      tl.to(blob, {
        opacity: 1,
        scale: 1,
        duration: 1,
        delay: index * 0.15, // Reduced stagger
        ease: "power2.out"
      });

      // Simplified continuous animations
      const rotation = gsap.to(blob, {
        duration: config.animationDuration.fast + index * 2,
        rotate: 360,
        repeat: -1,
        ease: "none",
        transformOrigin: "center center"
      });

      const floating = gsap.to(blob, {
        duration: config.animationDuration.slow,
        x: `random(-${config.movements.blob}vw, ${config.movements.blob}vw)`,
        y: `random(-${config.movements.blob}vh, ${config.movements.blob}vh)`,
        scale: `random(${config.scales.blob.min}, ${config.scales.blob.max})`,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut"
      });

      animationsRef.current.push(tl, rotation, floating);
    });

    // Create particle animations with reduced complexity
    particles.forEach((particle, index) => {
      const tl = gsap.timeline();
      
      // Entrance animation
      tl.to(particle, {
        opacity: 1,
        scale: 1,
        duration: 0.5,
        delay: index * 0.03, // Reduced stagger
        ease: "power2.out"
      });

      // Simplified particle movement
      const floating = gsap.to(particle, {
        duration: config.animationDuration.slow + index,
        x: `random(-${config.movements.particle}vw, ${config.movements.particle}vw)`,
        y: `random(-${config.movements.particle}vh, ${config.movements.particle}vh)`,
        scale: `random(${config.scales.particle.min}, ${config.scales.particle.max})`,
        opacity: `random(0.1, 0.25)`,
        repeat: -1,
        ease: "none"
      });

      animationsRef.current.push(tl, floating);
    });

    // Optimized parallax effect
    const parallaxTl = gsap.to([...blobs, ...particles], {
      y: (i, el) => (parseFloat(el.dataset.speed) || 1) * window.innerHeight * 0.5, // Reduced parallax intensity
      ease: "none",
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top top",
        end: "bottom top",
        scrub: 1,
        invalidateOnRefresh: true
      }
    });

    animationsRef.current.push(parallaxTl);
  }, [config]);

  useEffect(() => {
    if (!isInitialized) return;

    // Use requestIdleCallback for non-critical animations
    const initAnimations = () => {
      createOptimizedAnimations();
    };

    if (window.requestIdleCallback) {
      window.requestIdleCallback(initAnimations);
    } else {
      setTimeout(initAnimations, 100);
    }

    return () => {
      // Cleanup all animations
      animationsRef.current.forEach(anim => anim.kill());
      animationsRef.current = [];
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [isInitialized, createOptimizedAnimations]);

  // Memoize particles array to prevent recreation
  const particleElements = useMemo(() => 
    Array.from({ length: config.particleCount }, (_, i) => (
      <div
        key={i}
        ref={el => { if (el) particlesRef.current[i] = el; }}
        data-speed={0.1 + (i * 0.03)}
        className="absolute left-1/2 top-1/2 w-2 h-2 rounded-full bg-white/30 blur-sm"
        style={{ 
          willChange: 'transform, opacity',
          transform: 'translate3d(0,0,0)' // GPU acceleration
        }}
      />
    )), [config.particleCount]
  );

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 -z-10 overflow-hidden pointer-events-none"
      style={{ 
        perspective: 1000,
        willChange: 'transform'
      }}
    >
      {/* Optimized Gradient Blobs */}
      <div 
        ref={el => { if (el) blobsRef.current[0] = el; }}
        data-speed="0.5"
        className="absolute left-1/4 top-1/4 w-[35vw] h-[35vw] rounded-full bg-gradient-to-r from-blue-500/25 to-purple-500/25 blur-3xl"
        style={{ 
          willChange: 'transform, opacity',
          transform: 'translate3d(0,0,0)'
        }}
      />
      <div 
        ref={el => { if (el) blobsRef.current[1] = el; }}
        data-speed="0.8"
        className="absolute right-1/4 top-1/3 w-[30vw] h-[30vw] rounded-full bg-gradient-to-r from-red-500/15 to-orange-500/15 blur-3xl"
        style={{ 
          willChange: 'transform, opacity',
          transform: 'translate3d(0,0,0)'
        }}
      />
      <div 
        ref={el => { if (el) blobsRef.current[2] = el; }}
        data-speed="0.3"
        className="absolute left-1/3 bottom-1/4 w-[40vw] h-[40vw] rounded-full bg-gradient-to-r from-green-500/15 to-teal-500/15 blur-3xl"
        style={{ 
          willChange: 'transform, opacity',
          transform: 'translate3d(0,0,0)'
        }}
      />
      <div 
        ref={el => { if (el) blobsRef.current[3] = el; }}
        data-speed="0.6"
        className="absolute right-1/3 bottom-1/3 w-[45vw] h-[45vw] rounded-full bg-gradient-to-r from-indigo-500/20 to-purple-700/20 blur-3xl"
        style={{ 
          willChange: 'transform, opacity',
          transform: 'translate3d(0,0,0)'
        }}
      />
      <div 
        ref={el => { if (el) blobsRef.current[4] = el; }}
        data-speed="0.4"
        className="absolute left-1/5 bottom-1/5 w-[38vw] h-[38vw] rounded-full bg-gradient-to-r from-rose-500/15 to-orange-600/15 blur-3xl"
        style={{ 
          willChange: 'transform, opacity',
          transform: 'translate3d(0,0,0)'
        }}
      />
      <div 
        ref={el => { if (el) blobsRef.current[5] = el; }}
        data-speed="0.9"
        className="absolute right-1/6 top-1/6 w-[28vw] h-[28vw] rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-700/20 blur-3xl"
        style={{ 
          willChange: 'transform, opacity',
          transform: 'translate3d(0,0,0)'
        }}
      />

      {/* Optimized Particles */}
      {particleElements}

      {/* Optimized Noise Texture */}
      <div 
        className="absolute inset-0 opacity-[0.01] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='1'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '128px 128px' // Reduced texture size
        }}
      />
    </div>
  );
});

AnimatedBackground.displayName = 'AnimatedBackground';

export default AnimatedBackground; 